import React, { useMemo } from "react";
import { getAprStatusMeta } from "../aprWorkflowStats";

const STATUS_SORT = { none: 0, Start: 1, Pending: 2, Completed: 3, Closed: 4 };

const DistrictSubmissionTable = ({
  rows,
  loading,
  reportingYear,
  regionFilter,
  onRegionFilterChange,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  regionOptions,
  statusOptions,
}) => {
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ra = a.regionName.localeCompare(b.regionName);
      if (ra !== 0) return ra;
      const sa =
        (STATUS_SORT[getAprStatusMeta(a.status).key] ?? 0) -
        (STATUS_SORT[getAprStatusMeta(b.status).key] ?? 0);
      if (sa !== 0) return sa;
      return a.districtName.localeCompare(b.districtName);
    });
  }, [rows]);

  const submittedCount = rows.filter((r) => getAprStatusMeta(r.status).submittedToRcc).length;

  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-4 py-4 md:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground md:text-xl">
              District submission status
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {reportingYear} under review — {rows.length} district(s) shown
              {rows.length > 0 && (
                <span className="text-foreground">
                  {" "}
                  · {submittedCount} submitted to RCC or beyond
                </span>
              )}
            </p>
          </div>
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-3xl lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Region
              </label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={regionFilter}
                onChange={(e) => onRegionFilterChange(e.target.value)}
              >
                {regionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Status
              </label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Search
              </label>
              <input
                type="search"
                placeholder="District or region…"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-semibold md:px-6">Region</th>
              <th className="px-4 py-3 font-semibold md:px-6">District</th>
              <th className="px-4 py-3 font-semibold md:px-6">Report status</th>
              <th className="px-4 py-3 font-semibold md:px-6">Submitted to RCC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-muted-foreground md:px-6">
                  Loading district statuses…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-muted-foreground md:px-6">
                  No districts match the current filters.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const meta = getAprStatusMeta(row.status);
                return (
                  <tr key={row.districtId} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-muted-foreground md:px-6">{row.regionName}</td>
                    <td className="px-4 py-3 font-medium text-foreground md:px-6">{row.districtName}</td>
                    <td className="px-4 py-3 md:px-6">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.badgeClass}`}
                      >
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 md:px-6">
                      {meta.submittedToRcc ? (
                        <span className="font-medium text-emerald-700">Yes</span>
                      ) : (
                        <span className="text-muted-foreground">No</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default DistrictSubmissionTable;
